import { supabase } from '@shared/lib/supabase';
import { getCurrentProfileId } from '@shared/lib/profile-id';
import { type CommentsRepository } from '../application/comments.repository';
import { type WorkoutComment } from '../domain/comment';

interface CommentRow {
  id: string;
  session_id: string;
  author_profile_id: string;
  body: string;
  read_at: string | null;
  created_at: string;
}

interface ProfileRefRow {
  id: string;
  display_name: string | null;
  auth_user_id: string | null;
}

async function annotateAuthors(rows: CommentRow[]): Promise<WorkoutComment[]> {
  const authorIds = Array.from(new Set(rows.map((r) => r.author_profile_id)));
  const profilesById = new Map<string, ProfileRefRow>();
  if (authorIds.length > 0) {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, display_name, auth_user_id')
      .in('id', authorIds);
    if (error) throw new Error(error.message);
    for (const p of data) profilesById.set(p.id, p);
  }
  return rows.map((row) => {
    const profile = profilesById.get(row.author_profile_id);
    return {
      id: row.id,
      sessionId: row.session_id,
      authorProfileId: row.author_profile_id,
      authorDisplayName: profile?.display_name ?? '—',
      authorIsGhost: profile?.auth_user_id === null,
      body: row.body,
      readAt: row.read_at,
      createdAt: row.created_at,
    };
  });
}

export const supabaseCommentsRepository: CommentsRepository = {
  async listForSession(sessionId: string): Promise<WorkoutComment[]> {
    const { data, error } = await supabase
      .from('workout_comments')
      .select('id, session_id, author_profile_id, body, read_at, created_at')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });
    if (error) throw new Error(error.message);
    return annotateAuthors(data);
  },

  async create(sessionId: string, body: string): Promise<WorkoutComment> {
    const me = await getCurrentProfileId();
    const { data, error } = await supabase
      .from('workout_comments')
      .insert({ session_id: sessionId, author_profile_id: me, body: body.trim() })
      .select('id, session_id, author_profile_id, body, read_at, created_at')
      .single();
    if (error) throw new Error(error.message);
    const annotated = await annotateAuthors([data]);
    return annotated[0]!;
  },

  async markRead(commentIds: readonly string[]): Promise<void> {
    if (commentIds.length === 0) return;
    const { error } = await supabase
      .from('workout_comments')
      .update({ read_at: new Date().toISOString() })
      .in('id', [...commentIds])
      .is('read_at', null);
    if (error) throw new Error(error.message);
  },

  async updateBody(commentId: string, body: string): Promise<WorkoutComment> {
    const trimmed = body.trim();
    const { data, error } = await supabase
      .from('workout_comments')
      .update({ body: trimmed })
      .eq('id', commentId)
      .select('id, session_id, author_profile_id, body, read_at, created_at')
      .single();
    if (error) throw new Error(error.message);
    const annotated = await annotateAuthors([data]);
    return annotated[0]!;
  },

  async remove(commentId: string): Promise<void> {
    const { error } = await supabase.from('workout_comments').delete().eq('id', commentId);
    if (error) throw new Error(error.message);
  },

  async countUnreadOnMine(): Promise<number> {
    const me = await getCurrentProfileId();
    // Sessions owned by me that have unread comments authored by someone else.
    // Two-step query — RLS already restricts to readable rows.
    const { data: sessionRows, error: sessionsError } = await supabase
      .from('workout_sessions')
      .select('id')
      .eq('user_id', me);
    if (sessionsError) throw new Error(sessionsError.message);
    const sessionIds = sessionRows.map((s) => s.id);
    if (sessionIds.length === 0) return 0;

    const { count, error } = await supabase
      .from('workout_comments')
      .select('*', { count: 'exact', head: true })
      .in('session_id', sessionIds)
      .neq('author_profile_id', me)
      .is('read_at', null);
    if (error) throw new Error(error.message);
    return count ?? 0;
  },
};
